import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MoreHorizontal, Check, X, Pencil, Ban, Receipt } from "lucide-react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { currencies } from "@/lib/constants";

export function HaafPaymentsTable({
  data,
  onVerificationToggle,
  onEdit,
  onDelete,
  onDownloadReceipt,
  hideVerificationToggle,
  showActions,
}) {
  const columns = [
    {
      accessorKey: "paymentId",
      header: "Payment ID",
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("paymentId")}</div>
      ),
    },
    {
      accessorKey: "payee",
      header: "Payee",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="font-medium">{row.getValue("payee")}</div>
          {row.original.phoneNumber && (
            <div className="text-xs text-muted-foreground">
              {row.original.phoneNumber}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: "batch",
      header: "Batch",
      cell: ({ row }) => <div>{row.getValue("batch")}</div>,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("amount"));
        const currency = row.original.currency || "INR";
        const formatted = amount.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        return (
          <div className="font-medium">
            {currencies[currency]?.symbol || ""}
            {formatted}
            <span className="ml-1 text-xs text-muted-foreground">
              {currency}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "chapter.chapterName",
      header: "Chapter",
      cell: ({ row }) => <div>{row.original.chapter?.chapterName || "—"}</div>,
    },
    {
      accessorKey: "createdBy.chapterName",
      header: "Created By",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.original.createdBy?.user?.email || "—"}</span>
          <span className="text-xs text-muted-foreground">
            {row.original.createdBy?.role}
            {row.original.createdBy?.chapterName
              ? ` (${row.original.createdBy.chapterName})`
              : ""}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "verified",
      header: "Status",
      cell: ({ row }) => {
        if (row.original.status === "Cancelled") {
          return <Badge variant="destructive">Cancelled</Badge>;
        }

        return row.getValue("verified") ? (
          <Badge className="bg-green-500">Verified</Badge>
        ) : (
          <Badge variant="outline">Pending</Badge>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <div>
              {format(new Date(row.getValue("createdAt")), "dd/MM/yyyy")}
            </div>
            <div className="text-xs text-muted-foreground">
              {format(new Date(row.getValue("createdAt")), "h:mm a")}
            </div>
          </div>
        );
      },
    },
    {
      id: "receipt",
      header: () => <div className="text-center">Receipt</div>,
      cell: ({ row }) => {
        return (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDownloadReceipt(row.original)}
              className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
              title="Download Receipt"
            >
              <Receipt className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
    ...(showActions
      ? [
          {
            id: "actions",
            cell: ({ row }) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    {!hideVerificationToggle && (
                      <>
                        <DropdownMenuItem
                          onClick={() => onVerificationToggle(row.original)}
                        >
                          {row.original.verified ? (
                            <>
                              <X className="mr-2 h-4 w-4" /> Unverify
                            </>
                          ) : (
                            <>
                              <Check className="mr-2 h-4 w-4" /> Verify
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => onEdit(row.original)}>
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                    {row.original.status !== "Cancelled" && (
                      <DropdownMenuItem
                        onClick={() => onDelete(row.original)}
                      >
                        <Ban className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]
      : []),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

HaafPaymentsTable.propTypes = {
  data: PropTypes.array.isRequired,
  onVerificationToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownloadReceipt: PropTypes.func.isRequired,
  hideVerificationToggle: PropTypes.bool,
  showActions: PropTypes.bool,
};
